import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import mixpanel from 'mixpanel-browser';
import { MixpanelEvent } from '../constants/mixpanel-events.const';

@Injectable({
  providedIn: 'root',
})
export class MixpanelService {
  private readonly MIXPANEL_TOKEN = '1e01eb18a3db52a3b88544140d173b49';
  private router = inject(Router);

  constructor() {
    this.initializeMixpanel();
  }

  private initializeMixpanel(): void {
    mixpanel.init(this.MIXPANEL_TOKEN, {
      debug: true,
      track_pageview: true,
      persistence: 'localStorage',
    });
  }

  private static isValidPropertyObject(
    obj: unknown,
  ): obj is Record<string, unknown> {
    return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
  }

  track<T extends Record<string, unknown>>(
    eventName: string | MixpanelEvent,
    properties?: T,
  ): void {
    if (properties && !MixpanelService.isValidPropertyObject(properties)) {
      throw new Error('Invalid properties object');
    }

    const enrichedProperties: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      rota: this.router.url,
      ...properties,
    };

    mixpanel.track(eventName, enrichedProperties);
  }

  identify<T extends Record<string, unknown>>(
    userId: string,
    properties?: T,
  ): void {
    mixpanel.identify(userId);
    if (properties) {
      mixpanel.people.set(properties);
    }
  }

  setUserProperties<T extends Record<string, unknown>>(properties: T): void {
    mixpanel.people.set(properties);
  }

  trackPageView<T extends Record<string, unknown>>(
    pageName: string,
    properties?: T,
  ): void {
    this.track(MixpanelEvent.TELA_VISUALIZADA, {
      url: pageName,
      ...properties,
    });
  }

  reset(): void {
    mixpanel.reset();
  }

  optOut(): void {
    mixpanel.opt_out_tracking();
  }

  optIn(): void {
    mixpanel.opt_in_tracking();
  }

  setSuperProperties<T extends Record<string, unknown>>(properties: T): void {
    mixpanel.register(properties);
  }
}
